import { useMemo } from "react";
import { makeDemoCdf } from "@/utils/cdf";

type Props = {
  mean: number;
  std: number;
  value: number; // user height in cm
};

const BellCurveSvg = ({ mean, std, value }: Props) => {
  const path = useMemo(() => {
    const points: string[] = [];
    const xMin = mean - 4 * std;
    const xMax = mean + 4 * std;
    const width = 300;
    const height = 120;
    const scaleX = (x: number) => ((x - xMin) / (xMax - xMin)) * width;
    const normPdf = (x: number) =>
      (1 / (std * Math.sqrt(2 * Math.PI))) *
      Math.exp(-0.5 * Math.pow((x - mean) / std, 2));
    let maxY = 0;
    for (let x = xMin; x <= xMax; x += (xMax - xMin) / 200) {
      maxY = Math.max(maxY, normPdf(x));
    }
    const scaleY = (y: number) => height - (y / maxY) * (height - 10);
    for (let x = xMin; x <= xMax; x += (xMax - xMin) / 200) {
      const y = normPdf(x);
      points.push(`${scaleX(x)},${scaleY(y)}`);
    }
    return { d: `M ${points.join(" L ")}`, width, height, scaleX };
  }, [mean, std]);

  const xMin = mean - 4 * std;
  const xMax = mean + 4 * std;
  const clamp = (v: number) => Math.max(xMin, Math.min(xMax, v));
  const xPos = ((clamp(value) - xMin) / (xMax - xMin)) * 300;
  const anchor: 'start' | 'end' = xPos > 260 ? 'end' : 'start';
  const offset = anchor === 'start' ? 6 : -6;

  return (
    <svg viewBox={`0 0 ${300} ${120}`} width="100%" height="120" role="img" aria-label="Bell curve visualization">
      <path d={path.d} fill="none" className="stroke-primary/60" strokeWidth={2} />
      {/* user marker */}
      <line
        x1={xPos}
        x2={xPos}
        y1={10}
        y2={115}
        className="stroke-success"
        strokeWidth={3}
      />
      <text
        x={xPos + offset}
        y={20}
        fill="currentColor"
        className="text-success text-[11px] font-medium select-none"
        textAnchor={anchor}
      >
        You are here
      </text>
    </svg>
  );
};

export default BellCurveSvg;
